import { useChapterEntities } from '@/services/query/plotcard-entities-queries';
import { cn } from '@/lib/utils';
import { ChevronRight, Layers, MapPin, Users, Box, Component } from 'lucide-react';

interface ChapterEntitiesTabProps {
  chapterId?: string | number;
}

import { SidebarHeader } from '@/components/ui/sidebar';

export function ChapterEntitiesTab({ chapterId }: ChapterEntitiesTabProps) {
  const { data: entities = [], isLoading } = useChapterEntities(Number(chapterId));

  const groupedEntities = entities.reduce((acc, entity) => {
    const category = entity.category_name || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(entity);
    return acc;
  }, {} as Record<string, typeof entities>);

  const categoryPriority: Record<string, number> = {
    'Character': 1,
    'Location': 2,
    'Setting': 3,
    'Object': 4,
    'Uncategorized': 99
  };

  const sortedCategories = Object.keys(groupedEntities).sort((a, b) => {
    const priorityA = categoryPriority[a] || 10;
    const priorityB = categoryPriority[b] || 10;
    return priorityA !== priorityB ? priorityA - priorityB : a.localeCompare(b);
  });

  const getCategoryConfig = (category: string) => {
    switch (category.toLowerCase()) {
      case 'character':
        return { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: Users };
      case 'location':
      case 'setting':
        return { color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: MapPin };
      case 'object':
        return { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Box };
      default:
        return { color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20', icon: Component };
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center p-8 text-center text-muted-foreground min-h-0">
          <span className="animate-pulse text-xs">Loading entities...</span>
        </div>
      );
    }

    if (entities.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground min-h-0">
          <div className="w-12 h-12 rounded-2xl bg-muted/50 flex items-center justify-center mb-4 ring-1 ring-border/50">
            <Layers className="h-6 w-6 text-muted-foreground/30" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">No Entities Linked</p>
          <p className="text-xs max-w-[200px] leading-relaxed opacity-60">
            Mention characters or locations in your chapter to track them here.
          </p>
        </div>
      );
    }

    return (
      <div className="flex-1 w-full overflow-y-auto p-4 custom-scrollbar space-y-6 min-h-0">
        {sortedCategories.map(category => {
        const items = groupedEntities[category];
        const config = getCategoryConfig(category);
        const Icon = config.icon;

        return (
            <div key={category} className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                    <span className={cn("p-1 rounded-md", config.bg)}>
                        <Icon className={cn("size-3", config.color)} />
                    </span>
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80">
                        {category} <span className="opacity-50 ml-1">· {items.length}</span>
                    </h4>
                </div>

                <div className="grid gap-2">
                    {items.map(entity => (
                        <div
                            key={entity.entity_id}
                            className={cn(
                                "group relative flex items-start gap-3 p-3 rounded-xl border border-border/40 bg-card/40 hover:bg-accent/40 hover:border-accent transition-all duration-200 cursor-pointer overflow-hidden"
                            )}
                        >
                             <div className={cn(
                                 "absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b opacity-0 group-hover:opacity-100 transition-opacity",
                                 config.bg.replace('/10', '/50')
                             )} />

                            <div className={cn(
                                "h-8 w-8 rounded-full flex items-center justify-center border flex-shrink-0 bg-background/80 backdrop-blur-sm shadow-sm",
                                config.border
                            )}>
                                <span className={cn("text-xs font-bold", config.color)}>
                                    {entity.entity_name.charAt(0)}
                                </span>
                            </div>

                            <div className="min-w-0 flex-1 pt-0.5">
                                <div className="flex items-center justify-between gap-2">
                                    <h5 className="text-[13px] font-medium text-foreground group-hover:text-primary transition-colors truncate">
                                        {entity.entity_name}
                                    </h5>
                                </div>
                                {entity.entity_description && (
                                    <p className="text-[11px] text-muted-foreground opacity-70 line-clamp-2 mt-0.5 leading-relaxed">
                                        {entity.entity_description}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
        })}
        <div className="h-10" />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full bg-transparent">
      <SidebarHeader className="h-12 flex flex-row items-center justify-between border-b px-4 py-0 shrink-0">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Layers className="h-4 w-4" />
          <span>Entities</span>
        </div>
      </SidebarHeader>
      {renderContent()}
    </div>
  );
}
